/**
 * Keadaan sesi absensi — menentukan tombol mana yang masuk akal ditampilkan.
 *
 * Aturannya diambil dari server (`AttendanceService`): sesi dianggap TERBUKA bila event
 * **paling baru** milik pengguna bertipe `CHECK_IN`. Perhatikan bahwa server memakai event
 * paling baru **lintas hari**, bukan event paling baru hari ini — sesi yang melewati tengah
 * malam tetap terbuka esok paginya, dan check-out-nya diwarisi ke `work_date` sesi dibuka.
 *
 * Menyalin aturan "hari ini saja" akan menampilkan tombol Check In kepada orang yang sesinya
 * masih terbuka sejak semalam, lalu server menolaknya dengan ATT-002 tanpa penjelasan yang
 * dapat ia hubungkan.
 */

export interface SessionEvent {
  readonly eventType: 'CHECK_IN' | 'CHECK_OUT';
  readonly occurredAtServer: string;
  readonly workDate: string;
}

export type NextAction = 'CHECK_IN' | 'CHECK_OUT';

export interface SessionState {
  readonly hasOpenSession: boolean;
  readonly nextAction: NextAction;
  /** Event terakhir, bila ada — dasar tampilan "terakhir tercatat…". */
  readonly latest: SessionEvent | null;
  /** Benar bila sesi terbuka berasal dari hari kerja sebelum `todayWorkDate`. */
  readonly carriedOverFromPreviousDay: boolean;
}

/**
 * TIDAK DIPAKAI LAYAR MANA PUN sejak 22 Agustus 2026 — dan sebaiknya tetap begitu.
 *
 * Beranda dulu memakainya untuk MENYIMPULKAN keadaan sesi dari daftar event. Masalahnya bukan
 * fungsi ini; masalahnya adalah `GET /api/attendance/me` tidak pernah mengirim event, sehingga
 * kesimpulannya selalu "tidak diketahui". Sekarang keadaan sesi diminta langsung ke
 * `GET /api/me/attendance/today`, yang menghitungnya dengan kebijakan dan zona waktu organisasi.
 *
 * Dipertahankan beserta ujinya karena aturan sesi-lintas-tengah-malam yang dikandungnya masih
 * benar dan mahal untuk ditemukan ulang. Tetapi bila suatu saat Anda tergoda memakainya lagi di
 * layar: server sudah menjawab pertanyaan yang sama, dan dua penghitung untuk satu fakta akan
 * berselisih diam-diam.
 *
 * Urutan ditentukan `occurredAtServer` — waktu SERVER, bukan waktu perangkat. Jam ponsel yang
 * salah tidak boleh mengubah event mana yang dianggap terakhir.
 */
export function deriveSessionState(
  events: readonly SessionEvent[],
  todayWorkDate: string,
): SessionState {
  if (events.length === 0) {
    return {
      hasOpenSession: false,
      nextAction: 'CHECK_IN',
      latest: null,
      carriedOverFromPreviousDay: false,
    };
  }

  const latest = [...events].sort(
    (a, b) => Date.parse(a.occurredAtServer) - Date.parse(b.occurredAtServer),
  )[events.length - 1]!;

  const hasOpenSession = latest.eventType === 'CHECK_IN';
  return {
    hasOpenSession,
    nextAction: hasOpenSession ? 'CHECK_OUT' : 'CHECK_IN',
    latest,
    carriedOverFromPreviousDay: hasOpenSession && latest.workDate < todayWorkDate,
  };
}

/**
 * Tanggal hari ini pada timezone ORGANISASI, sebagai `YYYY-MM-DD`.
 *
 * `toISOString()` akan memberi tanggal UTC — pada 07:30 WIB itu masih benar, tetapi pada
 * 06:30 WIB tanggal 1 ia mengembalikan tanggal 30 bulan sebelumnya. Kesalahan itu memindahkan
 * hari kerja seseorang, bukan sekadar salah tampil.
 */
export function todayInZone(now: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  // en-CA menghasilkan YYYY-MM-DD — format yang sama dengan `work_date` di server.
  return parts;
}

/** Rentang tanggal untuk menyusun keadaan hari ini: mundur beberapa hari agar sesi lintas
 *  tengah malam ikut terbaca. Tanpa ini, sesi semalam tidak terlihat dan tombolnya salah. */
export function todayLookbackRange(
  todayWorkDate: string,
  lookbackDays = 2,
): {
  readonly from: string;
  readonly to: string;
} {
  const [y, m, d] = todayWorkDate.split('-').map(Number) as [number, number, number];
  const from = new Date(Date.UTC(y, m - 1, d - lookbackDays));
  const iso = from.toISOString().slice(0, 10);
  return { from: iso, to: todayWorkDate };
}
